import { compareVersions, validate } from "compare-versions";
import { FEATURE_VERSION_MAP } from "@constants/testConstants/versionConstants";
import { IProduct, PlayerPrefUpdateResult } from "types/productManagementTypes";

export type ConfigKeyOption = "unset" | "dev" | "demo" | "uae" | "swe" | "default";
export type ConfigKeyOverride = Exclude<ConfigKeyOption, "unset">;

export const CONFIG_KEY_VALUES: ConfigKeyOption[] = [
  "unset",
  "dev",
  "demo",
  "uae",
  "swe",
  "default",
];

type Translate = (key: string) => string;

export const getConfigKeyOptions = (t: Translate): { value: ConfigKeyOption; label: string }[] =>
  CONFIG_KEY_VALUES.map((value) => ({ value, label: t(`DeviceConfig.configKey.${value}`) }));

export const getConfigKeyOverrideOptions = (
  t: Translate,
): { value: ConfigKeyOverride; label: string }[] =>
  getConfigKeyOptions(t).filter(
    (option): option is { value: ConfigKeyOverride; label: string } => option.value !== "unset",
  );

export const normalizeConfigKey = (value: unknown): ConfigKeyOption => {
  const str = typeof value === "string" ? value.trim().toLowerCase() : "";
  return CONFIG_KEY_VALUES.includes(str as ConfigKeyOption) ? (str as ConfigKeyOption) : "unset";
};

export const normalizeConfigKeyOverride = (value: unknown): ConfigKeyOverride | null => {
  const normalized = normalizeConfigKey(value);
  return normalized === "unset" ? null : normalized;
};

export const normalizeConfigKeyToken = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized && !/\s/.test(normalized) ? normalized : null;
};

export const defaultConfigKeyForIntegration = (integrationKey: unknown): string | null =>
  normalizeConfigKeyToken(integrationKey);

export const parseDeviceBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
};

export const configKeyToValue = (option: ConfigKeyOption): string =>
  option === "unset" ? "" : option;

export const CONFIG_KEY_PLAYER_PREF = "configKey" as const;
export const RESET_KEY_CONFIG_COMMAND = "resetKeyConfig" as const;

export type LogModeOption = "off" | "quiet" | "default" | "debug" | "verbose";

export const LOG_MODE_VALUES: LogModeOption[] = ["off", "quiet", "default", "debug", "verbose"];

export const getLogModeOptions = (t: Translate): { value: LogModeOption; label: string }[] =>
  LOG_MODE_VALUES.map((value) => ({ value, label: t(`DeviceConfig.logMode.${value}`) }));

export const normalizeLogMode = (value: unknown): LogModeOption => {
  const str = typeof value === "string" ? value.trim().toLowerCase() : "";
  return LOG_MODE_VALUES.includes(str as LogModeOption) ? (str as LogModeOption) : "default";
};

export type PlayerPrefKey =
  | "enableLogger"
  | "enableFileLogger"
  | "enableLokiLogger"
  | "logMode"
  | typeof CONFIG_KEY_PLAYER_PREF
  | "disableKeyConfigOnTest";
export type PlayerPrefChannelKey = PlayerPrefKey | typeof RESET_KEY_CONFIG_COMMAND;

// enableLogger and configKey are stored from 2.8.4 on, but only 2.8.5 applies
// them live through ApplyConfigKeyLive, which is what these controls promise.
export const PLAYER_PREF_MIN_VERSION: Record<PlayerPrefChannelKey, string> = {
  enableLogger: FEATURE_VERSION_MAP.playerPrefLiveApply,
  configKey: FEATURE_VERSION_MAP.playerPrefLiveApply,
  disableKeyConfigOnTest: FEATURE_VERSION_MAP.disableKeyConfigOnTest,
  [RESET_KEY_CONFIG_COMMAND]: FEATURE_VERSION_MAP.disableKeyConfigOnTest,
  enableFileLogger: FEATURE_VERSION_MAP.logSinkPrefs,
  enableLokiLogger: FEATURE_VERSION_MAP.logSinkPrefs,
  logMode: FEATURE_VERSION_MAP.logSinkPrefs,
};

export const parseDeviceBoolWithDefault = (value: unknown, fallback: boolean): boolean =>
  value === undefined || value === null || value === "" ? fallback : parseDeviceBool(value);

// validate() first: a device reporting a dev build ("2.8.6_dev") is not valid
// semver and would make compareVersions throw.
export const supportsHeadsetVersion = (
  minVersion: string,
  headsetVersion?: string | null,
): boolean =>
  typeof headsetVersion === "string" &&
  validate(headsetVersion) &&
  compareVersions(headsetVersion, minVersion) >= 0;

export const supportsPlayerPref = (
  key: PlayerPrefChannelKey,
  headsetVersion?: string | null,
): boolean => supportsHeadsetVersion(PLAYER_PREF_MIN_VERSION[key], headsetVersion);

// Backends before SD-1782 answered 409 for an offline headset instead of
// saving the pref; those deployments still exist.
export const isDeviceOfflineError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 409;

export type PlayerPrefDelivery = "delivered" | "queued" | "undelivered";

// update-playerpref answers { saved, delivered }. Older backends answered a bare
// boolean, where only a literal true confirmed delivery and false meant lost.
export const playerPrefDelivery = (response?: { data?: unknown } | null): PlayerPrefDelivery => {
  const data = response?.data;
  if (data === true) return "delivered";
  if (!data || typeof data !== "object") return "undelivered";
  const result = data as Partial<PlayerPrefUpdateResult>;
  if (result.delivered === true) return "delivered";
  return result.saved === true ? "queued" : "undelivered";
};

export const PLAYER_PREF_KEYS = Object.keys(PLAYER_PREF_MIN_VERSION).filter(
  (key): key is PlayerPrefKey => key !== RESET_KEY_CONFIG_COMMAND,
);

// Values saved on the backend but not yet echoed back by the headset.
export const pendingPlayerPrefValues = (
  product?: Pick<IProduct, "pendingPlayerPrefs"> | null,
): Partial<Record<PlayerPrefKey, string>> => {
  const pending = product?.pendingPlayerPrefs ?? {};
  const values: Partial<Record<PlayerPrefKey, string>> = {};
  PLAYER_PREF_KEYS.forEach((key) => {
    const value = pending[key]?.value;
    if (typeof value === "string") values[key] = value;
  });
  return values;
};
